import {
    usePathPopulate,
    getWidthProfileScale,
    WIDTH_PROFILE_LABELS,
    type WidthProfilePreset,
} from "./PathPopulateContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { useRef, useEffect, useMemo } from "react";
import {
    FileUp,
    Grid3x3,
    Merge,
    Palette,
    Plus,
    Route,
    Ruler,
    Share2,
    Shapes,
    Spline,
    Trash2,
} from "lucide-react";
import { OBJECT_SVG_PRESETS } from "../../utils/pathPopulatePresets";
import { isValidSvg } from "../../utils/svgUtils";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";

function WidthProfilePreview({
    profile,
    minScale,
}: {
    profile: WidthProfilePreset;
    minScale: number;
}) {
    const pathD = useMemo(() => {
        const w = 200;
        const h = 40;
        const steps = 40;
        const points: string[] = [];
        for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const scale = getWidthProfileScale(t, profile, minScale);
            const x = t * w;
            const y = h / 2 - (scale * h) / 2;
            points.push(
                `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`,
            );
        }
        for (let i = steps; i >= 0; i--) {
            const t = i / steps;
            const scale = getWidthProfileScale(t, profile, minScale);
            const x = t * w;
            const y = h / 2 + (scale * h) / 2;
            points.push(`L${x.toFixed(1)},${y.toFixed(1)}`);
        }
        points.push("Z");
        return points.join(" ");
    }, [profile, minScale]);

    return (
        <svg
            viewBox="0 0 200 40"
            width="200"
            height="40"
            className="width-profile-preview-svg"
        >
            <path d={pathD} fill="#555" />
        </svg>
    );
}

export function PathPopulateControls() {
    const {
        objectSvgs,
        pathSvg,
        distance,
        scale,
        scaleRandomness,
        distanceOffsetRandomness,
        perpendicularScatterPx,
        error,
        rotationMode,
        baseRotationDeg,
        polarRotationCenterXPct,
        polarRotationCenterYPct,
        showPaths,
        gridEnabled,
        showGrid,
        gridSize,
        anchor,
        gradientEnabled,
        gradientMode,
        gradientColors,
        maxGradientSteps,
        gradientPerPathRepeats,
        objectPlacementMode,
        widthProfileEnabled,
        widthProfile,
        widthProfileMode,
        widthProfileMinScale,
        centerSizeFalloffEnabled,
        centerSizeFalloffEdgeScale,
        mergeUnionOnExport,
        setMergeUnionOnExport,
        setDistance,
        setScale,
        setScaleRandomness,
        setDistanceOffsetRandomness,
        setPerpendicularScatterPx,
        setRotationMode,
        setBaseRotationDeg,
        setPolarRotationCenterXPct,
        setPolarRotationCenterYPct,
        setShowPaths,
        setGridEnabled,
        setShowGrid,
        setGridSize,
        setAnchor,
        setGradientEnabled,
        setGradientMode,
        setGradientColors,
        setMaxGradientSteps,
        setGradientPerPathRepeats,
        setObjectPlacementMode,
        setWidthProfileEnabled,
        setWidthProfile,
        setWidthProfileMode,
        setWidthProfileMinScale,
        setCenterSizeFalloffEnabled,
        setCenterSizeFalloffEdgeScale,
        addObjectSvg,
        removeObjectSvg,
        setPathSvg,
        setError,
        objectInputRefs,
        pathInputRef,
        handleFileLoad,
        pathViewBox,
        pathSegmentCount,
        applyPreset,
        getPresetValues,
        resetParameters,
        canExport,
        buildExportSvgString,
    } = usePathPopulate();

    const handleDistanceChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setDistance(numValue);
    };

    const handleScaleChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setScale(numValue);
    };

    const importObjectFiles = (files: File[] | FileList) => {
        for (const file of Array.from(files)) {
            const reader = new FileReader();
            reader.onload = () => {
                const text = typeof reader.result === "string" ? reader.result : "";
                if (!text) return;
                addObjectSvg({
                    name: file.name,
                    content: text,
                });
            };
            reader.readAsText(file);
        }
    };

    const importPathFile = (files: File[] | FileList) => {
        const file = Array.from(files)[0];
        if (!file) return;

        setError(null);

        if (
            !file.type.includes("svg") &&
            !file.name.toLowerCase().endsWith(".svg")
        ) {
            setError("Please choose an SVG file.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const text = typeof reader.result === "string" ? reader.result : "";
            if (!text) {
                setError("Unable to read SVG file.");
                return;
            }
            if (!isValidSvg(text)) {
                setError("Selected file does not appear to be valid SVG.");
                return;
            }
            setPathSvg({
                name: file.name,
                content: text,
            });
        };
        reader.onerror = () => {
            setError("Error reading file.");
        };
        reader.readAsText(file);
    };

    const handleScaleRandomnessChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0 || numValue > 100)
            return;
        setScaleRandomness(numValue);
    };

    const handleDistanceOffsetRandomnessChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0) return;
        setDistanceOffsetRandomness(numValue);
    };

    const handlePerpendicularScatterPxChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0) return;
        setPerpendicularScatterPx(numValue);
    };

    const handleBaseRotationChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        setBaseRotationDeg(numValue);
    };

    const handleGridSizeChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setGridSize(numValue);
    };

    const handlePolarRotationCenterXPctChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        setPolarRotationCenterXPct(numValue);
    };

    const handlePolarRotationCenterYPctChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        setPolarRotationCenterYPct(numValue);
    };

    const handleWidthProfileMinScaleChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0 || numValue > 1) return;
        setWidthProfileMinScale(numValue);
    };

    const handleCenterSizeFalloffEdgeScaleChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0 || numValue > 1) return;
        setCenterSizeFalloffEdgeScale(numValue);
    };

    const handleMaxGradientStepsChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 2 || numValue > 100)
            return;
        setMaxGradientSteps(numValue);
    };

    const handleGradientPerPathRepeatsChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 1) return;
        setGradientPerPathRepeats(Math.floor(numValue));
    };

    const handleAddColor = () => {
        setGradientColors([...gradientColors, "#000000"]);
    };

    const minPaletteColors =
        gradientMode === "random-per-path" ||
        gradientMode === "random-palette-per-instance"
            ? 1
            : 2;

    const handleRemoveColor = (index: number) => {
        if (gradientColors.length > minPaletteColors) {
            const newColors = gradientColors.filter((_, i) => i !== index);
            setGradientColors(newColors);
        }
    };

    const handleMoveColorUp = (index: number) => {
        if (index > 0) {
            const newColors = [...gradientColors];
            [newColors[index - 1], newColors[index]] = [
                newColors[index],
                newColors[index - 1],
            ];
            setGradientColors(newColors);
        }
    };

    const handleMoveColorDown = (index: number) => {
        if (index < gradientColors.length - 1) {
            const newColors = [...gradientColors];
            [newColors[index], newColors[index + 1]] = [
                newColors[index + 1],
                newColors[index],
            ];
            setGradientColors(newColors);
        }
    };

    const handleColorChange = (index: number, color: string) => {
        const newColors = [...gradientColors];
        newColors[index] = color;
        setGradientColors(newColors);
    };

    // Debounced color change handlers for color picker inputs
    const colorTimeoutRefs = useRef<Map<number, ReturnType<typeof setTimeout>>>(
        new Map(),
    );

    useEffect(() => {
        // Cleanup timeouts on unmount
        return () => {
            colorTimeoutRefs.current.forEach((timeout) =>
                clearTimeout(timeout),
            );
            colorTimeoutRefs.current.clear();
        };
    }, []);

    const handleColorPickerChange = (index: number, color: string) => {
        // Clear existing timeout for this color index
        const existingTimeout = colorTimeoutRefs.current.get(index);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
        }

        // Set new timeout
        const timeout = setTimeout(() => {
            handleColorChange(index, color);
            colorTimeoutRefs.current.delete(index);
        }, 300);

        colorTimeoutRefs.current.set(index, timeout);
    };

    return (
        <div className="panel-controls path-populate-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Object Along Path</h2>
                    <ToolDescriptionPopover dialogLabel="Object Along Path tool description">
                        <p>
                            Load one or more <strong>object SVGs</strong> and a{" "}
                            <strong>path SVG</strong>. The object SVGs will be
                            placed along all paths, polygons, lines, and
                            rectangles in the path SVG according to the selected
                            placement mode. Use presets below or upload your own
                            files.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>
            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="object-along-path"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup title="Object SVGs" icon={Shapes}>
                    <label className="field-label">Object SVGs</label>
                    <p className="path-populate-preset-hint">
                        Quick add preset
                    </p>
                    <div className="path-populate-preset-chips">
                        {OBJECT_SVG_PRESETS.map((preset) => (
                            <button
                                key={preset.id}
                                type="button"
                                className="generic-button path-populate-preset-chip path-populate-preset-chip--icon"
                                title={`Add ${preset.label} as object`}
                                aria-label={`Add ${preset.label} as object`}
                                onClick={() => {
                                    if (!isValidSvg(preset.svg)) return;
                                    addObjectSvg({
                                        name: `${preset.label} (preset)`,
                                        content: preset.svg,
                                    });
                                }}
                            >
                                <span
                                    className="path-populate-preset-chip-icon"
                                    aria-hidden="true"
                                    dangerouslySetInnerHTML={{
                                        __html: preset.svg,
                                    }}
                                />
                            </button>
                        ))}
                    </div>
                    <div className="path-populate-object-rows">
                        {objectSvgs.map((svg, index) => (
                            <div
                                key={index}
                                className="path-populate-object-row"
                            >
                                <div className="upload-controls">
                                    <button
                                        type="button"
                                        className="tool-file-btn tool-file-btn--stretch"
                                        onClick={() => {
                                            const input =
                                                objectInputRefs.current?.[
                                                    index
                                                ];
                                            if (input) {
                                                input.click();
                                            }
                                        }}
                                    >
                                        <FileUp
                                            className="tool-file-btn__icon"
                                            size={18}
                                            aria-hidden
                                        />
                                        <span>
                                            {svg.name ||
                                                `Choose object SVG ${index + 1}...`}
                                        </span>
                                    </button>
                                    <input
                                        ref={(el) => {
                                            if (el && objectInputRefs.current) {
                                                objectInputRefs.current[index] =
                                                    el;
                                            }
                                        }}
                                        type="file"
                                        accept=".svg,image/svg+xml"
                                        className="upload-input"
                                        onChange={handleFileLoad(
                                            "object",
                                            index,
                                        )}
                                    />
                                </div>
                                {svg.name && (
                                    <span className="path-populate-object-name">
                                        {svg.name}
                                    </span>
                                )}
                                <button
                                    type="button"
                                    className="tool-action-btn tool-action-btn--danger"
                                    onClick={() => removeObjectSvg(index)}
                                >
                                    <Trash2
                                        className="tool-action-btn__icon"
                                        size={16}
                                        aria-hidden
                                    />
                                    <span>Remove</span>
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="tool-action-btn tool-action-btn--add"
                            onClick={() => {
                                const input = document.createElement("input");
                                input.type = "file";
                                input.multiple = true;
                                input.accept = ".svg,image/svg+xml";
                                input.style.display = "none";
                                input.addEventListener("change", (e) => {
                                    importObjectFiles(
                                        (e.target as HTMLInputElement).files ?? [],
                                    );
                                    document.body.removeChild(input);
                                });
                                document.body.appendChild(input);
                                input.click();
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "copy";
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                importObjectFiles(e.dataTransfer.files);
                            }}
                        >
                            <Plus
                                className="tool-action-btn__icon"
                                size={18}
                                strokeWidth={2.25}
                                aria-hidden
                            />
                            <span>Add object SVG</span>
                        </button>
                    </div>
                    {objectSvgs.length > 0 && (
                        <div className="field-row path-populate-placement-row">
                            <label className="field-label">
                                Object placement mode
                                <select
                                    value={objectPlacementMode}
                                    onChange={(e) =>
                                        setObjectPlacementMode(
                                            e.target.value as
                                                | "alternating"
                                                | "random",
                                        )
                                    }
                                    className="anchor-select"
                                >
                                    <option value="alternating">
                                        Alternating
                                    </option>
                                    <option value="random">Random</option>
                                </select>
                            </label>
                        </div>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Path SVG" icon={Route}>
                    <label className="field-label">Path SVG</label>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="tool-file-btn"
                            onClick={() => pathInputRef.current?.click()}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "copy";
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                importPathFile(e.dataTransfer.files);
                            }}
                        >
                            <FileUp
                                className="tool-file-btn__icon"
                                size={18}
                                aria-hidden
                            />
                            <span>Choose path SVG...</span>
                        </button>
                        <input
                            ref={pathInputRef}
                            type="file"
                            accept=".svg,image/svg+xml"
                            className="upload-input"
                            onChange={handleFileLoad("path")}
                        />
                    </div>
                    {pathSvg && (
                        <p className="upload-info">
                            Path: <strong>{pathSvg.name}</strong>
                            {pathViewBox && (
                                <>
                                    {" "}
                                    - viewBox: <code>{pathViewBox}</code>
                                </>
                            )}
                        </p>
                    )}
                    {!pathSegmentCount && pathSvg && (
                        <p className="upload-error">
                            No path geometry found (&lt;path&gt;,
                            &lt;polygon&gt;, &lt;polyline&gt;, &lt;line&gt;, or
                            &lt;rect&gt;) in path SVG.
                        </p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Spacing & rotation" icon={Ruler}>
                    <div className="field-row">
                        <label className="field-label">
                            Distance (px along path)
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={distance}
                                onChange={handleDistanceChange}
                                debounceMs={500}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Object size (px)
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={scale}
                                onChange={handleScaleChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Size randomness (%)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={scaleRandomness}
                                onChange={handleScaleRandomnessChange}
                                debounceMs={500}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Distance offset randomness (px)
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={0.5}
                                value={distanceOffsetRandomness}
                                onChange={handleDistanceOffsetRandomnessChange}
                                debounceMs={500}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Perpendicular scatter (px)
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={0.5}
                                value={perpendicularScatterPx}
                                onChange={handlePerpendicularScatterPxChange}
                                debounceMs={500}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Base rotation (deg)
                            <DebouncedInput
                                type="number"
                                step={5}
                                value={baseRotationDeg}
                                onChange={handleBaseRotationChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Object rotation
                            <select
                                value={rotationMode}
                                onChange={(e) =>
                                    setRotationMode(
                                        e.target.value as
                                            | "align"
                                            | "random"
                                            | "random-90"
                                            | "none"
                                            | "polar"
                                            | "polar-inverted",
                                    )
                                }
                                className="anchor-select"
                            >
                                <option value="align">
                                    Align to path direction
                                </option>
                                <option value="random">Random rotation</option>
                                <option value="random-90">
                                    Random rotation (90deg steps)
                                </option>
                                <option value="none">
                                    No rotation (base only)
                                </option>
                                <option value="polar">
                                    Polar around point (bottom=0)
                                </option>
                                <option value="polar-inverted">
                                    Polar around point (top=0)
                                </option>
                            </select>
                        </label>
                    </div>
                    {(rotationMode === "polar" ||
                        rotationMode === "polar-inverted") && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Polar point X (%)
                                    <DebouncedInput
                                        type="number"
                                        step={1}
                                        value={polarRotationCenterXPct}
                                        onChange={
                                            handlePolarRotationCenterXPctChange
                                        }
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Polar point Y (%)
                                    <DebouncedInput
                                        type="number"
                                        step={1}
                                        value={polarRotationCenterYPct}
                                        onChange={
                                            handlePolarRotationCenterYPctChange
                                        }
                                    />
                                </label>
                            </div>
                        </>
                    )}

                    <div className="field-row">
                        <label className="field-label">
                            Anchor point
                            <select
                                value={anchor}
                                onChange={(e) => setAnchor(e.target.value)}
                                className="anchor-select"
                            >
                                <option value="top-left">Top Left</option>
                                <option value="top">Top</option>
                                <option value="top-right">Top Right</option>
                                <option value="center-left">Center Left</option>
                                <option value="center">Center</option>
                                <option value="center-right">
                                    Center Right
                                </option>
                                <option value="bottom-left">Bottom Left</option>
                                <option value="bottom">Bottom</option>
                                <option value="bottom-right">
                                    Bottom Right
                                </option>
                            </select>
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="View & grid" icon={Grid3x3}>
                    <div className="field-row">
                        <CheckboxField
                            checked={showPaths}
                            onChange={setShowPaths}
                            label="Show original paths/polygons"
                            inline
                        />
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            checked={showGrid}
                            onChange={setShowGrid}
                            label="Show grid"
                            inline
                        />
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            checked={gridEnabled}
                            onChange={setGridEnabled}
                            label="Snap to grid"
                            inline
                        />
                    </div>

                    {(gridEnabled || showGrid) && (
                        <div className="field-row">
                            <label className="field-label">
                                Grid size (px)
                                <DebouncedInput
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={gridSize}
                                    onChange={handleGridSizeChange}
                                />
                            </label>
                        </div>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Width profile" icon={Spline}>
                    <div className="field-row">
                        <CheckboxField
                            checked={widthProfileEnabled}
                            onChange={setWidthProfileEnabled}
                            label="Enable width profile"
                            inline
                        />
                    </div>

                    {widthProfileEnabled && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Width profile preset
                                    <select
                                        value={widthProfile}
                                        onChange={(e) =>
                                            setWidthProfile(
                                                e.target
                                                    .value as WidthProfilePreset,
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        {(
                                            Object.keys(
                                                WIDTH_PROFILE_LABELS,
                                            ) as WidthProfilePreset[]
                                        ).map((key) => (
                                            <option key={key} value={key}>
                                                {WIDTH_PROFILE_LABELS[key]}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <WidthProfilePreview
                                    profile={widthProfile}
                                    minScale={widthProfileMinScale}
                                />
                            </div>

                            <div className="field-row">
                                <label className="field-label">
                                    Width profile mode
                                    <select
                                        value={widthProfileMode}
                                        onChange={(e) =>
                                            setWidthProfileMode(
                                                e.target.value as
                                                    | "per-path"
                                                    | "global",
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        <option value="per-path">
                                            Per path
                                        </option>
                                        <option value="global">Global</option>
                                    </select>
                                </label>
                            </div>

                            <div className="field-row">
                                <label className="field-label">
                                    Minimum scale
                                    <DebouncedInput
                                        type="number"
                                        min={0}
                                        max={1}
                                        step={0.05}
                                        value={widthProfileMinScale}
                                        onChange={
                                            handleWidthProfileMinScaleChange
                                        }
                                    />
                                </label>
                            </div>
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Center size falloff" icon={Spline}>
                    <div className="field-row">
                        <CheckboxField
                            checked={centerSizeFalloffEnabled}
                            onChange={setCenterSizeFalloffEnabled}
                            label="Bigger near center, smaller near edges"
                            inline
                        />
                    </div>

                    {centerSizeFalloffEnabled && (
                        <div className="field-row">
                            <label className="field-label">
                                Edge scale (relative to center)
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    value={centerSizeFalloffEdgeScale}
                                    onChange={
                                        handleCenterSizeFalloffEdgeScaleChange
                                    }
                                />
                            </label>
                        </div>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Color" icon={Palette}>
                    <div className="field-row">
                        <CheckboxField
                            checked={gradientEnabled}
                            onChange={setGradientEnabled}
                            label="Apply color along path"
                            inline
                        />
                    </div>

                    {gradientEnabled && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Color mode
                                    <select
                                        value={gradientMode}
                                        onChange={(e) =>
                                            setGradientMode(
                                                e.target.value as
                                                    | "per-path"
                                                    | "global"
                                                    | "random-per-path"
                                                    | "random-per-instance"
                                                | "random-palette-per-instance"
                                                | "source-path-color",
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        <option value="per-path">
                                            Gradient (per path)
                                        </option>
                                        <option value="global">
                                            Gradient (global)
                                        </option>
                                        <option value="random-per-path">
                                            Random solid per path
                                        </option>
                                        <option value="random-palette-per-instance">
                                            Random from palette per instance
                                        </option>
                                        <option value="random-per-instance">
                                            Random color per instance
                                        </option>
                                        <option value="source-path-color">
                                            Use source path color
                                        </option>
                                    </select>
                                </label>
                            </div>

                            {gradientMode !== "random-per-instance" &&
                                gradientMode !== "source-path-color" && (
                                <div className="field-row column">
                                    <label className="field-label">
                                        {gradientMode === "random-per-path" ||
                                        gradientMode ===
                                            "random-palette-per-instance"
                                            ? "Palette colors"
                                            : "Gradient colors"}
                                    </label>
                                    <div className="path-populate-gradient-colors">
                                        {gradientColors.map((color, index) => (
                                            <div
                                                key={index}
                                                className="path-populate-gradient-row"
                                            >
                                                <button
                                                    type="button"
                                                    className="path-populate-inline-btn"
                                                    onClick={() =>
                                                        handleMoveColorUp(index)
                                                    }
                                                    disabled={index === 0}
                                                    title="Move up"
                                                >
                                                    â†‘
                                                </button>
                                                <button
                                                    type="button"
                                                    className="path-populate-inline-btn"
                                                    onClick={() =>
                                                        handleMoveColorDown(
                                                            index,
                                                        )
                                                    }
                                                    disabled={
                                                        index ===
                                                        gradientColors.length -
                                                            1
                                                    }
                                                    title="Move down"
                                                >
                                                    ↓
                                                </button>
                                                <input
                                                    type="color"
                                                    value={color}
                                                    onChange={(e) =>
                                                        handleColorPickerChange(
                                                            index,
                                                            e.target.value,
                                                        )
                                                    }
                                                    style={{
                                                        width: "50px",
                                                        height: "30px",
                                                        minWidth: "30px",
                                                    }}
                                                />
                                                <DebouncedInput
                                                    type="text"
                                                    value={color}
                                                    onChange={(value) =>
                                                        handleColorChange(
                                                            index,
                                                            String(value),
                                                        )
                                                    }
                                                    style={{
                                                        flex: 1,
                                                        padding: "4px",
                                                        fontFamily: "monospace",
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="path-populate-inline-btn"
                                                    onClick={() =>
                                                        handleRemoveColor(index)
                                                    }
                                                    disabled={
                                                        gradientColors.length <=
                                                        minPaletteColors
                                                    }
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="path-populate-inline-btn"
                                            onClick={handleAddColor}
                                        >
                                            Add color
                                        </button>
                                    </div>
                                </div>
                                )}

                            {gradientMode !== "random-per-path" &&
                                gradientMode !== "random-per-instance" &&
                                gradientMode !==
                                    "random-palette-per-instance" &&
                                gradientMode !== "source-path-color" && (
                                    <div className="field-row">
                                        <label className="field-label">
                                            Max gradient steps
                                            <DebouncedInput
                                                type="number"
                                                min={2}
                                                max={100}
                                                step={1}
                                                value={maxGradientSteps}
                                                onChange={
                                                    handleMaxGradientStepsChange
                                                }
                                            />
                                        </label>
                                    </div>
                                )}

                            {gradientMode === "source-path-color" && (
                                <p className="path-populate-merge-note">
                                    Each placed object inherits the fill (or
                                    stroke if fill is none) from its source
                                    path segment.
                                </p>
                            )}

                            {gradientMode === "per-path" && (
                                <div className="field-row">
                                    <label className="field-label">
                                        Gradient repeats per path
                                        <DebouncedInput
                                            type="number"
                                            min={1}
                                            step={1}
                                            value={gradientPerPathRepeats}
                                            onChange={
                                                handleGradientPerPathRepeatsChange
                                            }
                                        />
                                    </label>
                                </div>
                            )}

                            {gradientMode === "random-per-path" && (
                                <p className="path-populate-merge-note">
                                    Each path in the path SVG gets one color
                                    from the palette (pseudo-random by path
                                    order; stable across preview and export).
                                </p>
                            )}

                            {gradientMode === "random-palette-per-instance" && (
                                <p className="path-populate-merge-note">
                                    Each instance picks one color from the
                                    palette (deterministic from placement;
                                    preview matches export).
                                </p>
                            )}

                            {gradientMode === "random-per-instance" && (
                                <p className="path-populate-merge-note">
                                    Each placed object gets its own fill color
                                    (deterministic from placement index; same
                                    colors in preview and export).
                                </p>
                            )}
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Export" icon={Merge}>
                    <div className="field-row">
                        <CheckboxField
                            checked={mergeUnionOnExport}
                            onChange={setMergeUnionOnExport}
                            label="Merge all instances to one path (boolean union) on export"
                            inline
                        />
                    </div>
                    {mergeUnionOnExport && (
                        <p className="path-populate-merge-note">
                            Per-instance fills, gradients, and random palette or
                            per-instance colors are not preserved: the export
                            uses a single fill taken from the object SVG (union
                            of all transformed shapes).
                        </p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => ({
                            name: "object-along-path-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["object-along-path"]}
                    />
                </CollapsibleToolGroup>
            </div>

            {error && <p className="upload-error">{error}</p>}
        </div>
    );
}
